import { Fragment, useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  CardActions,
  Button,
  Modal,
  Backdrop,
  Fade,
  CircularProgress
} from "@material-ui/core";
import { KeyboardBackspace, Close } from '@material-ui/icons';
import { CONSTANTS } from '../../constants/app-constant';
import ReactMarkdown from 'react-markdown';
import { getBackendAttribution } from '../../utils/attribution';
import { getExperimentPayload, trackExperimentExposure } from '../../utils/experiments';
import { api } from "../../services/apiService";
import { isMobileDevPaywallEnabled } from "../../constants/mobileBilling";
import { getAbsiteAppleTrialEligibility, getAbsiteMobilePackagePricing } from "../../services/mobileBillingService";
import { isNativeCapacitorRuntime } from "../../services/revenueCatService";
import "./subscriptions-list.scss";
import { REPLAY_MASK } from "../../utils/replay-mask";

// The render below sorts the list with this same comparator; keeping them
// identical is what lets the experiment preselect address plans by index.
const byPlanName = (a, b) => a.name.localeCompare(b.name, undefined, {
  numeric: true,
  sensitivity: 'base'
});

// apiService's doGetCall NEVER rejects: a network failure RESOLVES with the
// Error object, and a non-200 RESOLVES with {status, errMsg}. So eligibility
// must be classified from the RESOLVED shape — a try/catch around the call is
// dead code. Only a well-formed boolean payload is a definitive answer;
// anything else (Error instance, {status,…}/{…errMsg} error shape, or a
// malformed body) is UNKNOWN.
export const classifyTrialEligibility = (res) => {
  if (res === true || res === false) {
    return res;
  }
  return null; // unknown
};


// Human trial-length labels for the native offer copy (Akshay spec: the offer
// must read "1 week free", never a raw day count). Web copy is unchanged.
const getTrialLengthLabel = (trialPeriodDays) => {
  const labels = { 7: '1 week', 14: '2 weeks', 30: '1 month' };
  return labels[trialPeriodDays] || `${trialPeriodDays} days`;
};
export default function SubscriptionsListView(props) {
  const { packageList, userData, onConfirmation, onPaymentLoadSuccess, showPlanDetailsModal, hidePlanDetailsModal, isActivePlan, retryPlanId, onRetryConsumed, isMobileBilling, mobilePurchaseSuccess } = props;
  const [selectedSub, setSelectedSub] = useState(-1);
  const [selectedPackage, setSelectedPackage] = useState({});
  // const [selectedSubType, setSelectedSubType] = useState('BASIC');
  const [showPaymentBtn, setShowPaymentBtn] = useState(false);
  const [processType, setProcessType] = useState('sub-list');
  const [couponList, setCouponList] = useState([]);
  const [couponModal, setCouponModal] = useState(false);
  const [planDetailsModal, setPlanDetailsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState({});
  const [appliedCoupon, setAppliedCoupon] = useState({});
  const [planDetails, setPlanDetails] = useState({});
  const [trialEligible, setTrialEligible] = useState(false);
  // True only when the server said yes (never the optimistic unknown fallback).
  // The web review step names the trial only then; otherwise it stays neutral.
  const [trialConfirmed, setTrialConfirmed] = useState(false);
  // const history = useHistory();

  const onSubscriptionSelect = (evt, item, index) => {
    const clickedOnShowDetailsBtn = evt.target.className?.includes('show-plan-details');

    if (clickedOnShowDetailsBtn) {
      return;
    }

    setSelectedSub(index);
    setSelectedPackage(item);

    if (!showPaymentBtn) {
      setShowPaymentBtn(true);
    }
  };

  /*
   * pricing-pro-annual-default experiment: purely config-driven. The payload's
   * defaultSelectedPlanDurationDays picks which plan (if any) starts selected;
   * the control payload is null = today's no-preselection behavior, so with the
   * experiment flag off (or dark) this effect changes nothing. Reaching the plan
   * list IS the exposure (control users see the default too), so the emit sits
   * here — it no-ops unless the experiment is live, and dedups per unit.
   */
  useEffect(() => {
    // A B3 retry preselection owns the selection; don't let the experiment
    // default fight it.
    if (!packageList?.length || selectedSub !== -1 || retryPlanId) {
      return;
    }
    trackExperimentExposure('pricing-pro-annual-default', userData?.id);
    const { defaultSelectedPlanDurationDays } = getExperimentPayload('pricing-pro-annual-default', userData?.id);
    if (defaultSelectedPlanDurationDays === null || defaultSelectedPlanDurationDays === undefined) {
      return;
    }
    const sorted = [...packageList].sort(byPlanName);
    const defaultIndex = sorted.findIndex(
      (item) => item.price !== 0 && item.durationInDay === defaultSelectedPlanDurationDays
    );
    if (defaultIndex !== -1) {
      setSelectedSub(defaultIndex);
      setSelectedPackage(sorted[defaultIndex]);
      setShowPaymentBtn(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageList, userData?.id]);

  const onCheckout = () => {
    setProcessType('checkout');
    setLoading(false);
    window.scrollTo(0, 0);
  };

  const hideCheckout = () => {
    setProcessType('sub-list');
    setLoading(false);
  };

  const handleCouponModal = (open) => {
    setCouponModal(open);

    if (open) {
      setSelectedCoupon({});
    }
  };

  useEffect(() => {
    setLoading(onPaymentLoadSuccess);
  }, [onPaymentLoadSuccess]);

  useEffect(() => {
    if (showPlanDetailsModal) {
      setPlanDetails(showPlanDetailsModal);
      setPlanDetailsModal(true);
    }
  }, [showPlanDetailsModal]);

  // Mobile billing shows a single ABSITE product — auto-select it so the
  // Review Subscription CTA is immediately visible without a plan tap.
  useEffect(() => {
    if (isMobileBilling && packageList?.length > 0 && selectedSub === -1) {
      setSelectedSub(0);
      setSelectedPackage(packageList[0]);
      setShowPaymentBtn(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileBilling, packageList]);

  // Round-2 finding 1: the native paywall/checkout discloses the LOCALIZED
  // StoreKit price and the ACTUAL intro offer of the exact package being
  // bought — never the backend row's web figures. Until this resolves,
  // native price/trial copy is withheld and Subscribe is disabled (fail
  // closed); web rendering never consults it.
  const [storePricing, setStorePricing] = useState({ status: 'idle' });
  useEffect(() => {
    if (!isMobileBilling || !packageList?.length) {
      return undefined;
    }
    let cancelled = false;
    setStorePricing({ status: 'loading' });
    getAbsiteMobilePackagePricing({
      appUserID: userData?.id,
      productId: packageList[0]?.revenueCatProductId
    })
      .then((pricing) => {
        if (!cancelled) {
          setStorePricing(pricing?.ready ? { status: 'ready', ...pricing } : { status: 'failed', ...pricing });
        }
      })
      .catch(() => {
        if (!cancelled) setStorePricing({ status: 'failed' });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileBilling, packageList, userData?.id]);

  const storePricingReady = storePricing.status === 'ready';
  // Native trial truth = the package's FREE intro offer (0 when none/unknown);
  // the server/Apple eligibility gate still applies on top via trialEligible.
  const nativeTrialDays = storePricingReady ? storePricing.introTrialDays : 0;
  const hasNativeTrial = (plan) => (
    isMobileBilling && trialEligible && nativeTrialDays > 0 && plan?.trialPeriodDays !== 0
  );
  const nativePriceLabel = storePricingReady
    ? `${storePricing.priceString}${storePricing.billingCadence}`
    : null;
  // Zero-price ("Due today") disclosure in the BOUND package's own currency —
  // a hard-coded `$0.00` misstates the figure on non-USD storefronts. No
  // currencyCode from the store → the line is withheld (the trial disclosure
  // below the CTA still states the full offer).
  const nativeZeroPriceLabel = (() => {
    if (!storePricingReady || !storePricing.currencyCode) return null;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: storePricing.currencyCode
      }).format(0);
    } catch {
      return null;
    }
  })();


  useEffect(() => {
    // Trial copy stays hidden until the backend confirms this user never held
    // a paid plan — the server enforces the same rule at checkout.
    //
    // B2: distinguish "not eligible" (a definitive boolean `false` payload)
    // from "unknown" (network Error object, {status, errMsg} shape, malformed
    // body — doGetCall resolves ALL of these, it never rejects; see
    // classifyTrialEligibility). A transient hiccup must NOT silently degrade
    // an eligible user to the full-price wall: on unknown, retry once; if
    // still unknown, render the trial copy optimistically. The server
    // re-checks eligibility at checkout (resolveTrialPeriodDays) and the
    // Stripe checkout page shows the true amount due before card entry, so an
    // optimistic false-positive is corrected there rather than costing a
    // conversion up front.
    // Native runs the Apple-gated eligibility effect below instead — the
    // optimistic-on-unknown web fallback must never promise an App Store
    // trial Apple will not honor (runbook MUST-a, #541).
    if (isMobileBilling) {
      return undefined;
    }
    if (!userData?.id) {
      return undefined;
    }

    let cancelled = false;

    const fetchEligibility = () =>
      api.getTrialEligibility(userData.id)
        .then(classifyTrialEligibility)
        // Defensive only — the current apiService contract cannot reject.
        .catch(() => null);

    const resolveEligibility = async () => {
      let eligible = await fetchEligibility();
      if (eligible === null) {
        eligible = await fetchEligibility(); // exactly one retry
      }
      if (!cancelled) {
        // Still unknown after the retry → optimistic.
        setTrialEligible(eligible === null ? true : eligible);
        setTrialConfirmed(eligible === true);
      }
    };

    resolveEligibility();

    return () => {
      cancelled = true;
    };
  }, [isMobileBilling, userData?.id]);

  useEffect(() => {
    // Native-only: web's trial presentation ships from main (488fc77/616e4ed)
    // and this branch must leave web rendering untouched. Trial copy stays
    // hidden until the backend confirms this user never held a paid plan —
    // the server enforces the same rule at checkout. The local iOS preview
    // build has no backend user, so it short-circuits eligible to exercise
    // the trial presentation.
    if (!isMobileBilling) {
      return;
    }

    if (isMobileDevPaywallEnabled()) {
      setTrialEligible(true);
      return;
    }

    if (userData?.id) {
      // Runbook MUST-a (qlearn-unified#119): Apple's intro-offer eligibility is
      // the iOS source of truth for the free-week CTA — a reinstall or a second
      // QLearn account on the same Apple ID has no intro offer left and would
      // be billed $59 immediately, even when this QLearn account never trialed.
      // The server signal only continues to gate the copy; whenever Apple says
      // ineligible/unknown (or the check fails), no trial is promised.
      Promise.all([
        getAbsiteAppleTrialEligibility({ appUserID: userData.id }),
        api.getTrialEligibility(userData.id)
          .then((eligible) => eligible === true)
          .catch(() => false)
      ])
        .then(([appleEligible, serverEligible]) => setTrialEligible(appleEligible === true && serverEligible))
        .catch(() => setTrialEligible(false));
    }
  }, [isMobileBilling, userData?.id]);


  // B3 recovery: a failed checkout round-trips through the route with the plan
  // the user was buying (see subscriptions.jsx closeStatus). Re-select it and
  // reopen the SAME checkout so "Try Again" retries the same plan instead of
  // dumping the user on an empty plan list.
  //
  // The retry state is consumed EXACTLY ONCE: the ref guard makes parent
  // re-renders inert (packageList is a fresh array identity on every parent
  // render — without the guard, any redux settle would force-reopen a checkout
  // the user backed out of), and onRetryConsumed lets the parent strip the
  // route state (history.replace) so refresh/back-navigation can't replay the
  // forced-open checkout.
  const retryConsumedRef = useRef(false);
  useEffect(() => {
    if (retryConsumedRef.current || !retryPlanId || !packageList?.length) {
      return;
    }
    retryConsumedRef.current = true;
    const sorted = [...packageList].sort(byPlanName);
    const retryIndex = sorted.findIndex((item) => item.id === retryPlanId);
    if (retryIndex !== -1) {
      setSelectedSub(retryIndex);
      setSelectedPackage(sorted[retryIndex]);
      setShowPaymentBtn(true);
      setProcessType('checkout');
    }
    // Consume even when the plan is gone from the list (stale id): the state
    // must still be stripped so it can't replay.
    if (onRetryConsumed) {
      onRetryConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryPlanId, packageList]);


  const onCouponSelect = (item) => {
    setSelectedCoupon(item);
  };

  const onCouponApplied = () => {
    setAppliedCoupon(selectedCoupon);
    setCouponModal(false);
  };

  const getDiscountedPrice = (price) => {
    const priceAmt = parseFloat(price);

    if (appliedCoupon.discountType === 'percentage') {
      return priceAmt - (appliedCoupon.discountPercentage / 100 * priceAmt);
    } else {
      return priceAmt - appliedCoupon.discountAmount;
    }
  };

  const getSelectedPrice = () => {
    return selectedCoupon.couponCode ? getDiscountedPrice(selectedPackage.price) : selectedPackage.price;
  };

  const hasTrial = (plan) => {
    return trialEligible && plan?.trialPeriodDays > 0;
  };

  // Web only: the trial is confirmed for this student, so the review step can
  // say what starts and that checkout asks for a card.
  const hasConfirmedWebTrial = (plan) => !isMobileBilling && trialConfirmed && hasTrial(plan);

  const getBillingCadence = (plan) => {
    const durationInDay = parseInt(plan?.durationInDay, 10);

    if (Number.isNaN(durationInDay)) {
      return '';
    }

    if (durationInDay >= 28 && durationInDay <= 31) {
      return '/month';
    }

    if (durationInDay >= 365 && durationInDay <= 366) {
      return '/year';
    }

    return ` every ${durationInDay} days`;
  };

  const onCheckoutConfirmation = () => {
    setLoading(true);
    const attribution = getBackendAttribution();
    const payload = {
      planId: selectedPackage.id,
      userId: userData.id
    };

    if (isMobileBilling) {
      // Bind the confirmation to the exact StoreKit product the presented row
      // maps to — the purchase handler refuses an unbound row instead of
      // silently buying the default product (GPT-5.6 finding 1).
      payload.revenueCatProductId = selectedPackage.revenueCatProductId;
    }

    if (attribution) {
      payload.attribution = attribution;
    }

    if (isMobileBilling) {
      // The native purchase resolves to a typed result (success, cancel,
      // timeout, failure) — the Subscribe spinner must settle WITH it, or a
      // failed/timed-out purchase leaves the button spinning forever (gate
      // blocker on the watchdog PR). Web stays fire-and-forget: its
      // onConfirmation dispatches the Stripe session and loading is driven
      // by onPaymentLoadSuccess.
      Promise.resolve(onConfirmation(payload)).finally(() => setLoading(false));
      return;
    }

    onConfirmation(payload);
  };

  // Gate round-2 note: SPA-navigating the webview away from checkout loses
  // the purchase flow with no back affordance in the native shell. On native,
  // open legal pages in the system browser sheet (SFSafariViewController —
  // Done button returns to the exact checkout state); web keeps the anchor.
  const LEGAL_WEB_BASE = 'https://qlearn.medlearnity.com';

  const openLegalPage = async (evt, path) => {
    if (!isNativeCapacitorRuntime()) {
      return;
    }

    evt.preventDefault();

    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: `${LEGAL_WEB_BASE}${path}` });
    } catch {
      // Gate follow-up: a failed plugin chunk/bridge must never leave a dead
      // legal link on a 3.1.2 surface — fall back to in-app navigation
      // (loses checkout state, but the legal page is always reachable).
      window.location.assign(path);
    }
  };

  const showPlanDetails = (plan) => {
    setPlanDetails(plan);
    setPlanDetailsModal(true);
  };

  const hidePlanModal = () => {
    setPlanDetailsModal(false);
    hidePlanDetailsModal();
  };

  return (
    <Box className="sub-wrapper">
      {processType === 'sub-list' &&
        <>
          {!isMobileBilling &&
            <Typography variant="h6" className="plan-header">Choose your plan</Typography>
          }
          <Card className="subscription-card">
            <CardContent>
              <Typography variant="h6" className="new-plan-header">
                {isMobileBilling ? 'Choose your plan' : 'Available QLearn Plans'}
              </Typography>

              {packageList?.length > 0 &&
                <>
                  <List component="nav" className="sub-list">
                    {[...packageList].sort(byPlanName).map((item, index) => {
                      return <Fragment key={index}>
                        {item.price !== 0 &&
                          <Fragment>
                            <ListItem button
                              className={(isMobileBilling ? hasNativeTrial(item) : hasTrial(item)) ? 'trial-plan-item' : ''}
                              selected={selectedSub === index}
                              onClick={(evt) => onSubscriptionSelect(evt, item, index)}>
                              <ListItemText primary={item.name}
                                secondary={<>
                                  {(isMobileBilling ? hasNativeTrial(item) : hasTrial(item)) &&
                                    <>
                                      <Typography variant="caption" component="span" display="block" className="trial-offer-text">
                                        {isMobileBilling
                                          ? `${getTrialLengthLabel(nativeTrialDays)} free, then ${storePricing.priceString}${storePricing.billingCadence}`
                                          : `${item.trialPeriodDays} days free, then $${item.price}${getBillingCadence(item)}`}
                                      </Typography>
                                      <Typography variant="caption" component="span" display="block" className="trial-cancel-text">
                                        {/* Apple's 24-hour auto-renew rule applies to App Store trials only. */}
                                        {isMobileBilling
                                          ? "Cancel at least 24 hours before your trial ends and you won't be charged."
                                          : "Cancel anytime before your trial ends — you won't be charged."}
                                      </Typography>
                                    </>
                                  }
                                  {isMobileBilling
                                    ? <>
                                      <Typography variant="caption" component="span" display="block" className="plan-benefit">
                                        {item.questionsPerDay === 'Unlimited'
                                          ? 'Unlimited practice questions every day'
                                          : `${item.questionsPerDay} practice questions every day`}
                                      </Typography>
                                      <Typography variant="caption" component="span" display="block" className="plan-benefit">
                                        Detailed answer guidance and performance tracking
                                      </Typography>
                                    </>
                                    : <Typography variant="caption">
                                      Daily Question Limit: {item.questionsPerDay}
                                    </Typography>}{!isMobileBilling && <>, &nbsp;</>}
                                  <Typography variant="caption" className={isMobileBilling ? 'plan-benefit plan-renews' : ''}>
                                    {/* Native: renewal framing beats a raw duration dump next to
                                        "then $X/month" (UX-sweep B3) — and a native row must NEVER
                                        fall back to the backend duration figure (S1 class rule:
                                        StoreKit-localized or withheld); web copy unchanged. */}
                                    {isMobileBilling
                                      ? (getBillingCadence(item) === '/month'
                                        ? 'Renews monthly · cancel anytime'
                                        : 'Renews automatically · cancel anytime')
                                      : `Duration: ${CONSTANTS.durationObj[item.durationInDay] || item.durationInDay}`}
                                  </Typography>
                                  <Typography variant="caption" className="show-plan-details"
                                    onClick={() => showPlanDetails(item)}>Show Details</Typography>
                                </>}
                              />
                              <Box className="price-cont">
                                {!appliedCoupon.couponCode &&
                                  (isMobileBilling
                                    ? (storePricingReady &&
                                      <Typography className="price-text">{storePricing.priceString}<span className="price-cadence">{storePricing.billingCadence}</span></Typography>)
                                    : <Typography className="price-text">${item.price}<span className="price-cadence">{getBillingCadence(item)}</span></Typography>)
                                }
                                {appliedCoupon.couponCode &&
                                  <>
                                    <Typography className="new-price-text">${getDiscountedPrice(item.price)} </Typography>
                                    <Typography className="price-text deselected">${item.price} </Typography>
                                  </>
                                }
                              </Box>
                            </ListItem>

                            {!isMobileBilling && (
                              <Box p={2}>
                                <Button variant="contained" color="primary" disabled={!!isActivePlan}
                                  onClick={() => {
                                    setSelectedSub(index);
                                    setSelectedPackage(item);
                                    setShowPaymentBtn(true);
                                    onCheckout();
                                  }}>
                                  Review {item.name}
                                </Button>
                              </Box>
                            )}
                            <Divider />
                          </Fragment>
                        }
                      </Fragment>
                    })}
                  </List>
                  <Typography color="textSecondary" variant="caption" display="block">
                    {/* Mobile copy avoids Stripe-flow phrasing (Guideline 3.1.1):
                        purchases go through the App Store and sync to the account. */}
                    {isMobileBilling
                      ? 'Your subscription will activate upon purchase and sync to your QLearn account.'
                      : 'Your plan will activate upon purchase. You can purchase and switch between other plans at any time.'}
                  </Typography>
                </>
              }

              {packageList?.length === 0 &&
                <Typography color="textSecondary" variant="caption" display="block">
                  {/* An entitled account owning the only product is not an
                      error state — don't say "No plans found." at it. */}
                  {isActivePlan
                    ? 'Your subscription is active — there are no other plans to purchase.'
                    : 'No plans found.'}
                </Typography>
              }

            </CardContent>

            {showPaymentBtn &&
              <CardActions>
                {/* <Button color="primary" onClick={() => handleCouponModal(true)}>
                  Apply Coupon
                </Button> */}
                <Box className="checkout-btn-cont">
                  <Button variant="contained" color="primary" onClick={onCheckout} disabled={isActivePlan}>
                    {isMobileBilling
                      ? (hasNativeTrial(selectedPackage) ? 'Start free trial' : 'Review Subscription')
                      : 'Review selected plan'}
                  </Button>
                  {isActivePlan && <Typography variant="caption">You already have an active plan.</Typography>}
                </Box>

              </CardActions>
            }
          </Card>
        </>
      }
      {processType === 'checkout' &&
        <Box className={`checkout-wrapper ${(isMobileBilling ? hasNativeTrial(selectedPackage) : hasTrial(selectedPackage)) ? 'trial-checkout' : ''}`}>
          <Box className="checkout-header">
            <KeyboardBackspace onClick={hideCheckout} color="primary" />
            <Box className="checkout-header-cont">
              {/* Once the purchase completed, "almost done / before you
                  proceed" (and the trial pitch) contradict the Subscribed
                  state still on screen — confirm instead. */}
              {mobilePurchaseSuccess
                ? <>
                  <Typography className="bold checkout-title">You're all set!</Typography>
                  <Typography variant="caption" className="checkout-subline">Your subscription is active — head back to start your next quiz.</Typography>
                </>
                : hasNativeTrial(selectedPackage)
                  ? <>
                    {/* Akshay-specced NATIVE trial framing: the offer reads as a
                        clear value statement, never a plan-name mashup ("1-week
                        pro"). Native-only. Web has its own trial header below,
                        shown only when the server CONFIRMED eligibility; an
                        unknown answer keeps the neutral header (gate leg GPT-5.6
                        finding 2: never promise a trial on a guess). Trial length
                        and price come from the BOUND StoreKit package, never the
                        backend row (round-2 finding 1). */}
                    <Typography className="bold checkout-title">
                      Try Pro free for {getTrialLengthLabel(nativeTrialDays)}
                    </Typography>
                    <Typography variant="caption" className="checkout-subline">
                      Then {nativePriceLabel}. Cancel at least 24 hours before your trial ends and you won't be charged.
                    </Typography>
                  </>
                  : hasConfirmedWebTrial(selectedPackage)
                    ? <>
                      {/* Web trial: say what the student is starting and that
                          checkout asks for a card, before Stripe does. Shown only
                          when the server CONFIRMED eligibility (an unknown answer
                          keeps the neutral header); the renewal price stays on
                          screen in the lines below. */}
                      <Typography className="bold checkout-title">
                        Start your {selectedPackage.trialPeriodDays}-day free trial of {selectedPackage.name}
                      </Typography>
                      <Typography variant="caption" className="checkout-subline" data-testid="web-trial-subline">
                        $0 today. Secure checkout asks for a card; cancel before the trial ends and you won't be charged.
                      </Typography>
                    </>
                    : <>
                      <Typography className="bold checkout-title">{isMobileBilling ? "You're almost done!" : "Review your plan"}</Typography>
                      <Typography variant="caption" className="checkout-subline">Make sure the below information is correct before you proceed</Typography>
                    </>
              }
            </Box>
          </Box>
          <Box className="checkout-details">
            <Box className="checkout-details-items">
              <Typography>Package: <b>{selectedPackage.name}</b></Typography>
              {isMobileBilling ? (
                storePricingReady ? (
                  hasNativeTrial(selectedPackage) ? (
                    <>
                      {nativeZeroPriceLabel &&
                        <Typography>Due today: <b>{nativeZeroPriceLabel}</b></Typography>
                      }
                      <Typography>After {nativeTrialDays}-day trial: <b>{nativePriceLabel}</b></Typography>
                    </>
                  ) : (
                    <Typography>Price: <b>{nativePriceLabel}</b></Typography>
                  )
                ) : (
                  <Typography data-testid="store-pricing-pending">
                    {storePricing.status === 'failed'
                      ? 'App Store pricing is unavailable right now — purchasing is disabled. Please try again later.'
                      : 'Loading App Store pricing…'}
                  </Typography>
                )
              ) : hasTrial(selectedPackage) ? (
                <>
                  <Typography>Due today: <b>$0.00</b></Typography>
                  <Typography>After {selectedPackage.trialPeriodDays}-day trial: <b>${getSelectedPrice()}{getBillingCadence(selectedPackage)}</b></Typography>
                </>
              ) : (
                <Typography>Price: <b>${getSelectedPrice()}</b></Typography>
              )}
              {/* Masked: /subscriptions and /plans are recorded (M3), so no
                  route denial is behind this — and it renders in the CHECKOUT
                  panel, the screen every paying student passes through.
                  /user-settings is not recorded because it shows "name, email,
                  phone"; leaving the same address in plain text here would make
                  that cosmetic. */}
              <Typography {...REPLAY_MASK}>Email: <b>{userData.email}</b></Typography>
            </Box>
            {(isMobileBilling ? hasNativeTrial(selectedPackage) : hasTrial(selectedPackage)) &&
              <Typography className="trial-disclosure">
                {isMobileBilling
                  ? `${nativeTrialDays} days free, then ${nativePriceLabel}.`
                  : `${selectedPackage.trialPeriodDays} days free, then $${getSelectedPrice()}${getBillingCadence(selectedPackage)}. Cancel anytime.`}
              </Typography>
            }
            <Button variant="contained" color="primary" onClick={onCheckoutConfirmation}
              disabled={loading || mobilePurchaseSuccess || (isMobileBilling && !storePricingReady)} className="confirm-btn">
              {/* B5: after a successful native purchase the checkout stays open
                  with an explicit success state — freeze the button so a second
                  tap can't re-enter the purchase flow. */}
              {/* The web action opens secure checkout; it does not itself purchase. */}
              {mobilePurchaseSuccess
                ? 'Subscribed ✓'
                : (isMobileBilling
                  ? (hasNativeTrial(selectedPackage) ? 'Start Free Trial' : 'Subscribe')
                  : (hasConfirmedWebTrial(selectedPackage) ? 'Start free trial: $0 today' : 'Continue to secure checkout'))}
              {loading && (
                <CircularProgress size={24} className="loader" />
              )}
            </Button>
            {/* UX-sweep B1 (+ App Review 3.1.2): auto-renew subscriptions must
                carry billing/renewal disclosure and Terms/Privacy links at the
                point of purchase. Native only; plain anchors keep this
                component router-free. */}
            {isMobileBilling &&
              <Box className="checkout-legal">
                <Typography variant="caption" component="p" className="checkout-legal-text">
                  {/* With a free trial Apple charges when the trial ends, not
                      at confirmation. */}
                  {hasNativeTrial(selectedPackage)
                    ? 'Your Apple Account is charged when the free trial ends unless you cancel at least 24 hours before the trial ends.'
                    : 'Payment is charged to your Apple Account at confirmation.'}
                  {' '}Your subscription renews automatically until cancelled at least
                  24 hours before the end of the current period. Manage or cancel
                  anytime in your Apple Account settings.
                </Typography>
                <Box className="checkout-legal-links">
                  <a href="/terms" onClick={(evt) => openLegalPage(evt, '/terms')}>Terms of Use</a>
                  <span aria-hidden="true"> · </span>
                  <a href="/privacy-policy" onClick={(evt) => openLegalPage(evt, '/privacy-policy')}>Privacy Policy</a>
                </Box>
              </Box>
            }
          </Box>
        </Box>
      }

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className='coupon-modal sub-coupon-modal'
        open={couponModal}
        onClose={() => handleCouponModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={couponModal}>
          {/* Coupon codes are the student's own (M3 masks them). */}
          <Box className='modal-container' {...REPLAY_MASK}>
            <Typography variant="h5" className="header">Select a Coupon</Typography>
            <Box className="coupon-list">
              {couponList.length > 0 && couponList.map((item, index) =>
                <Box key={index}
                  onClick={() => onCouponSelect(item)}
                  className={item.couponCode === selectedCoupon.couponCode ? 'coupon-selected list-item' : 'list-item'}>
                  <Box className='coupon-dtls'>
                    Code: {item.couponCode}
                    <Box className='coupon-exp'>
                      Expiry: {new Date(item.expiryDate).toDateString()}
                    </Box>
                  </Box>

                  {item.discountType === 'percentage' &&
                    <Box className='coupon-amt'>
                      {item.discountPercentage}%
                    </Box>
                  }
                  {item.discountType !== 'percentage' &&
                    <Box className='coupon-amt'>
                      ${item.discountAmount}
                    </Box>
                  }
                </Box>
              )
              }
            </Box>

            {selectedCoupon.couponCode &&
              <Box className="apply-btn">
                <Button color="primary" onClick={() => handleCouponModal(false)}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={onCouponApplied}>
                  Apply
                </Button>
              </Box>
            }
          </Box>
        </Fade>
      </Modal>

      <Modal
        className='coupon-modal plan-details-modal'
        open={planDetailsModal}
        onClose={() => hidePlanModal()}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={planDetailsModal}>
          <Box className='modal-container'>
            <Button className="close-btn" size="small" onClick={() => hidePlanModal()}>
              <Close></Close>
            </Button>
            <Typography variant="h5" className="header">Plan Details</Typography>
            <Box className="plan-details-wrapper">
              <Box>
                <b>Name:</b> {planDetails.name}
              </Box>
              <Box>
                <b>Exam Types:</b> {planDetails.examTypes?.join(", ", " ")}
              </Box>
              {/* Native suppresses the backend Price/Duration lines: the modal
                  serves BOTH the offer rows (StoreKit figures live on the bound
                  row/checkout) and current-plan rows, whose purchase channel
                  (Stripe vs App Store) the row does not carry — either binding
                  would misstate one of them (S1 class rule). Web unchanged. */}
              {!isMobileBilling &&
                <>
                  <Box>
                    <b>Price:</b> {planDetails.price != null ? `$${planDetails.price}` : ''}
                  </Box>
                  <Box>
                    <b>Duration:</b> {CONSTANTS.durationObj[planDetails.durationInDay] || planDetails.durationInDay}
                  </Box>
                </>
              }
              <Box>
                <b>Questions Per Day:</b> {planDetails.questionsPerDay}
              </Box>
              {planDetails.description &&
                <Box>
                  <b>Description:</b> <br />
                  <ReactMarkdown>{planDetails.description}</ReactMarkdown>
                </Box>
              }
            </Box>

            <Box className="apply-btn">
              <Button variant="contained" color="primary" onClick={() => hidePlanModal()}>
                OK
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box >
  );
}
